// Dashboard overview API surface. M7c / P7.

import { api } from './api';

export interface SeriesPointDto {
  day: string;
  value: number;
}

export interface DashboardOverview {
  windowDays: number;
  from: string;
  to: string;
  people: {
    total: number;
    adults: number;
    learners: number;
    active: number;
    newInWindow: number;
    newInPrevWindow: number;
    byStatus: Record<string, number>;
    byLocale: Record<string, number>;
    signupSeries: SeriesPointDto[];
    unsubscribed: number;
    suppressedByReason: Record<string, number>;
  };
  geo: {
    /** Denominator for coverage — the UI must not imply a complete picture. */
    profilesTotal: number;
    withCountry: number;
    topCountries: Array<{ country: string; count: number }>;
  };
  money: {
    revenueByCurrency: Record<string, number>;
    revenueSeries: Array<{ currency: string; points: SeriesPointDto[] }>;
    payingSubs: number;
    subsByStatus: Record<string, number>;
    subsByPlan: Record<string, number>;
  };
  content: {
    pagesLive: number;
    postsPublished: number;
    postsDraft: number;
    media: number;
    lessons: number;
    subjects: number;
  };
  attention: {
    pastDue: number;
    openInvoices: number;
    cardsExpiring: number;
    draftPosts: number;
  };
  recentInvoices: Array<{
    id: string;
    amountMinor: number;
    currency: string;
    status: string;
    issuedAt: string;
    paidAt: string | null;
  }>;
  recentSignups: Array<{
    id: string;
    email: string | null;
    locale: string;
    status: string;
    createdAt: string;
  }>;
}

export async function getDashboardOverview(
  days: number,
  signal?: AbortSignal,
): Promise<DashboardOverview> {
  return api(`/admin/dashboard/overview?days=${days}`, { signal });
}

/** ISO 3166-1 alpha-2 → a readable name, for the countries we actually sell in. */
const COUNTRY_NAMES: Record<string, string> = {
  QA: 'Qatar',
  AE: 'United Arab Emirates',
  SA: 'Saudi Arabia',
  KW: 'Kuwait',
  BH: 'Bahrain',
  OM: 'Oman',
  EG: 'Egypt',
  JO: 'Jordan',
  US: 'United States',
  GB: 'United Kingdom',
  TR: 'Türkiye',
  MY: 'Malaysia',
  ID: 'Indonesia',
  PK: 'Pakistan',
  IN: 'India',
  FR: 'France',
  DE: 'Germany',
  CA: 'Canada',
};

export function countryName(code: string): string {
  return COUNTRY_NAMES[code.toUpperCase()] ?? code.toUpperCase();
}

/**
 * Regional-indicator flag emoji from an alpha-2 code.
 * Purely decorative — the name is always rendered beside it, because flag
 * glyphs do not render on every platform (notably Windows Chrome shows the
 * letters instead, which is a graceful fallback rather than a break).
 */
export function countryFlag(code: string): string {
  const cc = code.toUpperCase();
  if (!/^[A-Z]{2}$/.test(cc)) return '';
  return String.fromCodePoint(...[...cc].map((ch) => 0x1f1e6 + ch.charCodeAt(0) - 65));
}
